import { ApiRequests, ApiRequestsTypes } from '@tableau/catalog-ui-temp';
import { createHash } from 'crypto';

import { Telemetry, ValidTelemetryEvent } from '~commonInfra/services/Telemetry';
import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { assertUnreachable } from '~commonInfra/utils/TypescriptHelpers';
import { SearchSuggestionsService } from '~src/spacesPlatform/services/SearchSuggestionsService';

import { SessionSiteSelectors } from '../bootstrap/redux/selectors/SessionInfoSelectors';
import { getAppStore } from '../bootstrap/redux/store/AppStore';
import { Formatters, Messages } from '../codegen/Localize';
import { GetDatasourceActionsRequest } from '../datasource/ServerApi/requests/GetDatasourceActionsRequest';
import { IDatasourceActions } from '../datasource/ServerApi/types/IDatasourceActions';
import { GetFlowActionsRequest } from '../flow/serverApi/requests/GetFlowActionsRequest';
import { RestApiErrorResponse } from '../httpClient/RestApiService';
import { Request } from '../httpClient/serverApi/requests/Request';
import WebHttpClient from '../httpClient/WebHttpClient';
import { ISingleIdParams } from '../spacesPlatform/utils/actionParams';
import { SiteRolesUtil } from '../userManagement/services/resources/SiteRolesUtil';
import { GetWorkbookActionsRequest } from '../workbook/ServerApi/requests/GetWorkbookActionsRequest';
import { Tables } from './resources/Tables';
import { setDescriptionForItem } from './restApi/descriptions/setDescriptionForItem';
import {
    callViaApiService as setDrillPathDescriptionApi, EditDrillPathFieldDescriptionResponse
} from './restApi/EditDrillPathFieldDescription';
import {
    callViaApiService as setFieldDescriptionApi, EditFieldDescriptionResponse
} from './restApi/EditFieldDescription';
import { GetContainerForContentRequest } from './serverApi/requests/databases/GetContainerForContent';
import { GetDatabaseActionsRequest } from './serverApi/requests/databases/GetDatabaseActionsRequest';
import { GetTableActionsRequest } from './serverApi/requests/databases/GetTableActionsRequest';
import {
    GetAllowedClassesRequest, IGetAllowedClassesParams, IGetAllowedClassesResult
} from './serverApi/requests/GetAllowedClasses';
import {
    ISendImpactAnalysisEmailsParams, SendImpactAnalysisEmailsRequest
} from './serverApi/requests/SendImpactAnalysisEmailsRequest';
import { TableAllowedActions } from './serverApi/types/TableAllowedActions';

type CatalogActionsRequestArgs = {
  contentType: 'database' | 'table' | 'flow' | 'workbook' | 'datasource';
  contentId: string;
};

interface CatalogPermissionsResponse {
  sendEmail: boolean;
}

enum DatasourceFieldDialogTelemetryEvents {
  descriptionUpdate = 'DatasourceFieldDescriptionUpdate'
}

interface DatasourceFieldDialogDescriptionUpdateEvent
extends ValidTelemetryEvent {
  type: DatasourceFieldDialogTelemetryEvents.descriptionUpdate;
  obfuscated_datasource_field: string; // fully qualified name and datasource luid hashed
  datasource_luid: string; // luid of datasource of field
  elapsed_time: number; // time it took to complete request in millseconds
  is_error: boolean; // whether or not the update failed or not
  error_code: string; // the status code of the failure if provided
}


function getContentActionsRequest(args: CatalogActionsRequestArgs): Request<ISingleIdParams, CatalogPermissionsResponse> {
  switch (args.contentType) {
    case 'database': return new GetDatabaseActionsRequest(args.contentId);
    case 'table': return new GetTableActionsRequest(args.contentId);
    case 'flow': return new GetFlowActionsRequest(args.contentId);
    case 'workbook': return new GetWorkbookActionsRequest(args.contentId);
    case 'datasource': return new GetDatasourceActionsRequest(args.contentId);
    default: assertUnreachable(args.contentType);
  }
}

async function searchMatchingTags(args: ApiRequestsTypes.SearchMatchingTagsArgs): Promise<ReadonlyArray<string>> {
  const response = await SearchSuggestionsService.tags(args.partialText).getSlice(args.startIndex, args.maxItems);
  return response.items.map(t => t.name);
}

async function sendImpactAnalysisEmailAndShowToast(args: ISendImpactAnalysisEmailsParams): Promise<void> {
  return WebHttpClient.sendRequest(new SendImpactAnalysisEmailsRequest(args))
    .then((result: any) => {
      if (result && !result.errors) {
        ToasterService.instance.pop({ type: ToastType.Info, text: Messages.dataCatalogEmailSuccess() });
      }

      if (!result || result.errors) {
        ToasterService.instance.pop({ type: ToastType.Error, text: Messages.unexpectedError() });
      }
    });
}

async function setColumnDescription(args: ApiRequestsTypes.SetColumnDescriptionArgs): Promise<void> {
  return setDescriptionForItem({
    contentType: 'columns',
    luid: args.columnLuid,
    tableLuid: args.tableLuid,
    body: {
      column: {
        description: args.description,
      }
    }
  }).then((result) => {
    if (result && !result.errors) {
      ToasterService.instance.pop({ type: ToastType.Info, text: Messages.editDescriptionSuccessfulWithWait() });
      return Promise.resolve();
    } else {
      ToasterService.instance.pop({ type: ToastType.Error, text: Messages.editDescriptionFailure() });
      return Promise.reject();
    }
  });
}

async function setFieldDescription(args: ApiRequestsTypes.SetFieldDescriptionArgs): Promise<void> {
  const startTime = Date.now();

  return setFieldDescriptionApi(args)
    .then(
      // onfulfilled
      (result: EditFieldDescriptionResponse) => {
        ToasterService.instance.pop({ type: ToastType.Info, text: Messages.editDescriptionSuccessfulWithWait() });
        sendSetFieldDescriptionTelemetry(args, startTime, false, null);
        return Promise.resolve();
      },
      // onrejected
      (reason: RestApiErrorResponse) => {
        ToasterService.instance.pop({ type: ToastType.Error, text: Messages.editDescriptionFailure() });

        if (reason && 'status' in reason) {
          sendSetFieldDescriptionTelemetry(args, startTime, true, JSON.stringify(reason.status));
        } else {
          sendSetFieldDescriptionTelemetry(args, startTime, true, null);
        }
        return Promise.reject(reason);
      }
    );
}

async function setDrillPathFieldDescription(args: ApiRequestsTypes.SetDrillPathFieldDescriptionArgs): Promise<void> {
  const startTime = Date.now();

  return setDrillPathDescriptionApi(args)
    .then(
      // onfulfilled
      (result: EditDrillPathFieldDescriptionResponse) => {
        ToasterService.instance.pop({ type: ToastType.Info, text: Messages.editDescriptionSuccessfulWithWait() });
        sendSetFieldDescriptionTelemetry(args, startTime, false, null);
        return Promise.resolve();
      },
      // onrejected
      (reason: RestApiErrorResponse) => {
        ToasterService.instance.pop({ type: ToastType.Error, text: Messages.editDescriptionFailure() });

        if (reason && 'status' in reason) {
          sendSetFieldDescriptionTelemetry(args, startTime, true, JSON.stringify(reason.status));
        } else {
          sendSetFieldDescriptionTelemetry(args, startTime, true, null);
        }
        return Promise.reject(reason);
      }
    );
}

function sendSetFieldDescriptionTelemetry(args: ApiRequestsTypes.SetFieldDescriptionArgs, startTime: number, isError: boolean, errorCode: string) {
  const elapsedTime = Date.now() - startTime;

  const hash = createHash('sha256');
  const obfuscatedDatasourceField = hash.update(args.fullyQualifiedFieldName + args.datasourceLuid).digest('hex');

  Telemetry.sendEvent<DatasourceFieldDialogDescriptionUpdateEvent>({
    type: DatasourceFieldDialogTelemetryEvents.descriptionUpdate,
    obfuscated_datasource_field: obfuscatedDatasourceField,
    datasource_luid: args.datasourceLuid,
    elapsed_time: elapsedTime,
    is_error: isError,
    error_code: errorCode
  });
}

async function canUserSetColumnTags(args: ApiRequestsTypes.CanUserSetColumnTagsArgs): Promise<boolean> {
  return WebHttpClient.sendRequest(new GetTableActionsRequest(args.tableVizportalId))
    .then((result: TableAllowedActions) => {
      if (result) {
        return Promise.resolve(result.setDescription);
      } else {
        return Promise.reject();
      }
    });
}

async function canUserSetColumnDescription(args: ApiRequestsTypes.CanUserSetColumnDescriptionArgs): Promise<boolean> {
  return WebHttpClient.sendRequest(new GetTableActionsRequest(args.tableVizportalId))
    .then((result: TableAllowedActions) => {
      if (result) {
        return Promise.resolve(result.setDescription);
      } else {
        return Promise.reject();
      }
    });
}

async function canUserSetFieldDescription(args: ApiRequestsTypes.CanUserSetFieldDescriptionArgs): Promise<boolean> {
  return WebHttpClient.sendRequest(new GetDatasourceActionsRequest(args.datasourceVizportalId))
  .then((result: IDatasourceActions) => {
    if (result) {
      return Promise.resolve(result.setDescription);
    } else {
      return Promise.reject();
    }
  });
}

async function canUserSendImpactAnalysisEmail(args: CatalogActionsRequestArgs): Promise<boolean> {
  return WebHttpClient.sendRequest(getContentActionsRequest(args)).then(r => {
    if (r) {
      return Promise.resolve(r.sendEmail);
    } else {
      return Promise.reject();
    }
  });
}

async function getAllowedClasses(args: IGetAllowedClassesParams): Promise<IGetAllowedClassesResult> {
  return WebHttpClient.sendRequest(new GetAllowedClassesRequest(args));
}

function isSiteCreatorOrHigher(): boolean {
  const siteSessionRole = SessionSiteSelectors.siteRole(getAppStore().getState());
  if (siteSessionRole != null) {
    return SiteRolesUtil.isSiteCreatorOrHigher(siteSessionRole);
  } else {
    return false;
  }
}

function updateColumnTags(params: ApiRequestsTypes.ColumnTagsParams): Promise<void> {
  const  { luids, tagsToAdd, tagsToRemove } = params;
  return Tables.changeTagsOnColumns({ columnLuids: luids, tagsToAdd, tagsToRemove })
    .then(() => {
      ToasterService.instance.pop({
        type: ToastType.Info,
        text: Messages.changeTagsColumnsSuccess({
          ITEM_COUNT: luids.length,
          ITEM_COUNT_LOC: Formatters.formatCount(luids.length),
        })
      });
    })
    .catch(e => {
      const errorResponse = e as RestApiErrorResponse;
      ToasterService.instance.pop({
        type: ToastType.Error,
        text: Messages.changeTagsColumnsError({
          ITEM_COUNT: luids.length,
          ERROR_MESSAGE: errorResponse.data.error.detail,
        })
      });
      throw e;
    });
}

function getContainerForContent(args: ApiRequestsTypes.GetContainerForContentParams): Promise<ApiRequestsTypes.GetContainerForContentResult> {
  return WebHttpClient.sendRequest(new GetContainerForContentRequest(args));
}

export const CatalogApiRequests: ApiRequests = {
  searchMatchingTags,
  sendImpactAnalysisEmailAndShowToast,
  setColumnDescription,
  setFieldDescription,
  setDrillPathFieldDescription,
  canUserSetColumnTags,
  canUserSetColumnDescription,
  canUserSetFieldDescription,
  canUserSendImpactAnalysisEmail,
  getAllowedClasses,
  isSiteCreatorOrHigher,
  updateColumnTags,
  getContainerForContent,
};
